import { DataTable } from "@/components/molecules/tables/data-table";
import { Button } from "@/components/ui/button";
import {
  useAdminNumberRequests,
  type AdminNumberRequest,
  type NumberRequestWaitingOn,
} from "@/features/admin";
import { NumberRequestStateBadge } from "@/views/backoffice/connectors/components/number-requests/state-badge";
import { useTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AdminPageLayout } from "../layout";
import { CreateAdminNumberRequestDialog } from "./components/create-dialog";
import { WaitingOnBadge, waitingOnLabel } from "./waiting-on";

/**
 * The carrier paperwork of every tenant, in one queue.
 *
 * The tenant-facing page lives under each client's "Canaux" and is closed to
 * platform staff, who are members of their own tenant and of no other — so
 * before this page the half of the process that is ours (`documents_review` is
 * us checking the papers; `submitted`/`approved` are us chasing the carrier) had
 * to be worked one tenant at a time, from inside tenants we cannot enter.
 *
 * The server returns the whole table already ordered by what needs working
 * first, so the buckets below are a view over one fetch rather than five
 * queries — which is also what lets them carry counts.
 */

/** The buckets, in the order they are worth looking at. */
const BUCKETS: { key: NumberRequestWaitingOn | "all"; label: string }[] = [
  { key: "heyup", label: waitingOnLabel("heyup") },
  { key: "carrier", label: waitingOnLabel("carrier") },
  { key: "client", label: waitingOnLabel("client") },
  { key: "none", label: waitingOnLabel("none") },
  { key: "all", label: "Toutes" },
];

/** When the request last moved — what "stuck for three weeks" is read from. */
const lastMovedAt = (request: AdminNumberRequest): number => {
  const history = request.history?.items || [];
  return (
    history[history.length - 1]?.at ||
    request.updated_at ||
    request.created_at ||
    0
  );
};

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Age in whole days. Shown rather than a date because the queue is worked by
 * "what has been sitting the longest", and a date makes the reader do that
 * subtraction themselves on every row.
 */
const daysSince = (at: number): number =>
  at ? Math.floor((Date.now() - at) / DAY_MS) : 0;

function useColumns(): ColumnDef<AdminNumberRequest>[] {
  const { t } = useTranslation("numberRequests");

  return useMemo(
    () => [
      {
        accessorKey: "client_name",
        header: "Tenant",
        cell: ({ row }) => (
          <Link
            to={`/admin/number-requests/${row.original.client_id}/${row.original.id}`}
            className="font-medium hover:underline"
          >
            {row.original.client_name || row.original.client_id}
          </Link>
        ),
      },
      {
        accessorKey: "waiting_on",
        header: "En attente",
        cell: ({ row }) => (
          <WaitingOnBadge waitingOn={row.original.waiting_on} />
        ),
      },
      {
        accessorKey: "kind",
        header: t("numberRequests.columns.kind"),
        cell: ({ row }) => t(`numberRequests.kinds.${row.original.kind}`),
      },
      {
        accessorKey: "state",
        header: t("numberRequests.columns.state"),
        cell: ({ row }) => (
          <NumberRequestStateBadge state={row.original.state} />
        ),
      },
      {
        accessorKey: "documents_provided",
        header: t("numberRequests.columns.documents"),
        cell: ({ row }) =>
          row.original.documents_total ? (
            <span
              className={cn(
                "text-sm tabular-nums",
                row.original.documents_provided < row.original.documents_total
                  ? "text-muted-foreground"
                  : "text-foreground",
              )}
            >
              {row.original.documents_provided}/{row.original.documents_total}
            </span>
          ) : (
            <span className="text-muted-foreground text-sm">—</span>
          ),
      },
      {
        accessorKey: "phone_number",
        header: t("numberRequests.columns.number"),
        cell: ({ row }) =>
          row.original.phone_number ? (
            <span className="tabular-nums">{row.original.phone_number}</span>
          ) : (
            <span className="text-muted-foreground text-sm">
              {t("numberRequests.notAssignedYet")}
            </span>
          ),
      },
      {
        id: "age",
        header: () => <div className="text-right">Sans mouvement</div>,
        cell: ({ row }) => {
          const days = daysSince(lastMovedAt(row.original));
          const stale = row.original.waiting_on !== "none" && days >= 7;
          return (
            <div
              className={cn(
                "text-right text-sm tabular-nums",
                stale ? "font-medium text-red-500" : "text-muted-foreground",
              )}
            >
              {days === 0 ? "aujourd'hui" : `${days} j`}
            </div>
          );
        },
      },
      {
        id: "actions",
        header: () => <div className="text-right" />,
        cell: ({ row }) => (
          <div className="text-right">
            <Button asChild variant="outline" size="sm">
              <Link
                to={`/admin/number-requests/${row.original.client_id}/${row.original.id}`}
              >
                {t("numberRequests.actions.view")}
              </Link>
            </Button>
          </div>
        ),
      },
    ],
    [t],
  );
}

export function AdminNumberRequestsPage() {
  const [bucket, setBucket] = useState<NumberRequestWaitingOn | "all">("heyup");

  const { data = [], isLoading } = useAdminNumberRequests();
  const columns = useColumns();

  const counts = useMemo(() => {
    const byBucket = {
      client: 0,
      heyup: 0,
      carrier: 0,
      none: 0,
      all: data.length,
    };
    for (const request of data) byBucket[request.waiting_on]++;
    return byBucket;
  }, [data]);

  const rows = useMemo(
    () =>
      bucket === "all"
        ? data
        : data.filter((request) => request.waiting_on === bucket),
    [data, bucket],
  );

  return (
    <AdminPageLayout
      title="Demandes de numéro"
      description="Les demandes de numéro de tous les tenants, celles qui attendent une action de notre côté en premier."
      actions={<CreateAdminNumberRequestDialog />}
    >

      <div className="flex flex-wrap items-center gap-2">
        {BUCKETS.map((entry) => (
          <Button
            key={entry.key}
            variant={bucket === entry.key ? "default" : "outline"}
            size="sm"
            onClick={() => setBucket(entry.key)}
          >
            {entry.label}
            <span className="ml-1.5 tabular-nums opacity-70">
              {counts[entry.key]}
            </span>
          </Button>
        ))}
      </div>

      <DataTable
        columns={columns}
        data={rows}
        isLoading={isLoading}
      />
    </AdminPageLayout>
  );
}
